import { zodResolver } from "@hookform/resolvers/zod";
import { assertNever, ControlCategories } from "@theprelude/core";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownFormField,
  Form,
  Input,
  InputField,
  notifyError,
  notifySuccess,
  PlusIcon,
  ScrollAreaRoot,
  ScrollAreaViewport,
  ScrollBar,
} from "@theprelude/ds";
import { SCMCategories, SCMCategory } from "@theprelude/sdk";
import { Suspense, useState } from "react";
import {
  FieldArrayWithId,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import { z } from "zod";
import { TopLevelViewsSelector } from "@/components/top-level-views-selector";
import { useTopLevelSavedViews } from "@/hooks/use-top-level-saved-views";
import {
  Filter,
  filterSchema,
  odataOperatorSchema,
  TopLevelFilters,
} from "@/lib/filter";
import { isValidPythonRegex } from "@/lib/is-valid-python-regex";
import { parseFilter } from "@/lib/odata";
import { AvailableFilter, isDurationId } from "./available-filters";
import {
  convertFiltersToFormFilters,
  FilterFormField,
  formFilterValueSchema,
  getDefaultCast,
} from "./filter-form";

interface TopLevelViewGroup {
  name: string;
  displayName: string;
  views: Partial<Record<Category, any>>;
}

const TOP_LEVEL_VIEW_PREFIX = "$$__toplevel:";

export const isTopLevelView = (name: string) =>
  name.startsWith(TOP_LEVEL_VIEW_PREFIX);
export const getTopLevelViewName = (name: string) =>
  name.replace(TOP_LEVEL_VIEW_PREFIX, "");
export const createTopLevelViewName = (name: string) =>
  `${TOP_LEVEL_VIEW_PREFIX}${name}`;

// Category mapping to ControlCategories
export const CATEGORY_CONTROL_MAP = {
  endpoints: ControlCategories.XDR,
  users: ControlCategories.IDENTITY,
  inboxes: ControlCategories.EMAIL,
} as const;

const categorySchema = z.enum(["endpoints", "users", "inboxes"]);
export type Category = z.infer<typeof categorySchema>;

const formFilterSchema = filterSchema
  .extend({
    category: z.union([categorySchema, z.literal("")]),
    value: z.union([z.string(), z.null()]),
    operator: z.union([odataOperatorSchema, z.literal("")]),
    ors: z.array(formFilterValueSchema).optional(),
  })
  .refine(
    (input) =>
      input.operator === "" ||
      input.value === null ||
      (input.operator !== "is" && input.operator !== "is not") ||
      input.value !== "",
    {
      message: "String must contain at least 1 character(s)",
      path: ["value"],
    },
  )
  .refine(
    (input) =>
      (input.operator !== "matches" && input.operator !== "does not match") ||
      input.value === null ||
      isValidPythonRegex(input.value),
    { message: "Invalid regular expression", path: ["value"] },
  )
  .refine(
    (input) =>
      !isDurationId(input.id) ||
      input.value === null ||
      isValidDuration(input.value),
    {
      message: "Duration must be at least 1 day",
      path: ["value"],
    },
  );

const filterFormSchema = z.object({
  filters: z.array(formFilterSchema),
});

type FilterFormData = z.infer<typeof filterFormSchema>;
type FormFilter = z.infer<typeof formFilterSchema>;

const DEFAULT_FILTERS: FormFilter[] = [
  { id: "", operator: "", value: "", category: "", ors: [] },
];

type CategorizedAdditionalFilter = AvailableFilter & {
  category: Category;
};

type CategorizedFilter = Filter & {
  category: Category;
};

const isValidDuration = (duration?: string) => {
  if (!duration) {
    return true;
  }
  return Boolean(Number.parseInt(duration, 10));
};

function convertCategorizedFiltersToFormFilters(
  currentFilters: CategorizedFilter[],
  filters: CategorizedAdditionalFilter[],
): FormFilter[] {
  return convertFiltersToFormFilters(
    currentFilters,
    filters,
    (currentFilter, filter) => currentFilter.category === filter.category,
  );
}

function normalizedFilterToFilter(filter: CategorizedFilter): Filter {
  // biome-ignore lint/correctness/noUnusedVariables: Used in destructuring to remove category
  const { category, ...rest } = filter;
  return rest;
}

function convertFormFiltersToFilters(
  currentFilters: FormFilter[],
  filters: CategorizedAdditionalFilter[],
): TopLevelFilters {
  const normalizedFilters = currentFilters
    .map((f) => {
      const formField = filters.find(
        (c) => c.id === f.id && c.category === f.category,
      );
      const cast = formField?.cast ?? getDefaultCast(formField?.type);

      const filter = cast({ ...f, ...formField?.odata });
      return { ...filter, category: f.category };
    })
    .filter((f): f is Filter & { category: Category } => !!f.category);

  const grouped = Object.groupBy(normalizedFilters, (f) => f.category ?? "");

  return {
    endpoints: grouped.endpoints?.map(normalizedFilterToFilter),
    users: grouped.users?.map(normalizedFilterToFilter),
    inboxes: grouped.inboxes?.map(normalizedFilterToFilter),
  };
}

export function TopLevelFilterForm({
  availableFilters,
  onSubmit,
  onClose,
  onClear,
  defaultFilters,
}: {
  availableFilters: Record<Category, AvailableFilter[]>;
  onSubmit: (data: TopLevelFilters) => void;
  onClear: () => void;
  onClose: () => void;
  defaultFilters: TopLevelFilters;
}) {
  const {
    viewGroups,
    currentViewGroup,
    setCurrentViewGroup,
    createTopLevelView,
    updateTopLevelView,
    deleteTopLevelView,
    updateTopLevelViewName,
    isPendingSave,
    isPendingDelete,
  } = useTopLevelSavedViews();

  const [showNameDialog, setShowNameDialog] = useState(false);
  const [pendingFilters, setPendingFilters] = useState<TopLevelFilters | null>(
    null,
  );

  const nameForm = useForm<{ name: string }>({
    defaultValues: { name: "" },
  });

  const categorizedFilters: CategorizedAdditionalFilter[] = Object.entries(
    availableFilters,
  ).flatMap(([category, filters]) =>
    filters.map((f) => ({ ...f, category: category as Category })),
  );

  const categorizedCurrentFilters: CategorizedFilter[] = Object.entries(
    defaultFilters,
  ).flatMap(([category, filters]) =>
    filters.map((f) => ({ ...f, category: category as Category })),
  );

  const form = useForm<FilterFormData>({
    defaultValues: {
      filters:
        categorizedCurrentFilters.length > 0
          ? convertCategorizedFiltersToFormFilters(
              categorizedCurrentFilters,
              categorizedFilters,
            )
          : DEFAULT_FILTERS,
    },
    resolver: zodResolver(filterFormSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "filters",
  });

  // Convert TopLevelFilters to FormFilter array for the form
  const convertTopLevelFiltersToFormFilters = (
    filters: TopLevelFilters,
  ): FormFilter[] => {
    const formFilters: FormFilter[] = [];

    Object.entries(filters).forEach(([category, categoryFilters]) => {
      if (categoryFilters) {
        const converted = convertFiltersToFormFilters(
          categoryFilters.map((f) => ({
            ...f,
            category: category as Category,
          })),
          categorizedFilters,
        );
        formFilters.push(...converted);
      }
    });

    return formFilters.length > 0 ? formFilters : DEFAULT_FILTERS;
  };

  // Handle view group selection
  const handleViewGroupSelection = (viewGroup: TopLevelViewGroup | null) => {
    if (viewGroup) {
      setCurrentViewGroup(viewGroup.name);

      // Parse filters from all category views
      const topLevelFilters: TopLevelFilters = {};

      Object.entries(viewGroup.views).forEach(([category, view]) => {
        const filters = parseFilter(view.filter);
        if (filters.length > 0) {
          topLevelFilters[category as Category] = filters;
        }
      });

      const formFilters = convertTopLevelFiltersToFormFilters(topLevelFilters);
      form.reset({ filters: formFilters });
    } else {
      setCurrentViewGroup(null);
      form.reset({ filters: DEFAULT_FILTERS });
    }
  };

  async function onSave() {
    const formData = form.getValues();
    const filters = convertFormFiltersToFilters(
      formData.filters,
      categorizedFilters,
    );

    const selectedGroup = viewGroups.find((vg) => vg.name === currentViewGroup);

    if (!selectedGroup) {
      setPendingFilters(filters);
      setShowNameDialog(true);
      return;
    }

    await updateTopLevelView(selectedGroup, filters);
    notifySuccess({ message: "View updated successfully!" });
  }

  // Handle name form submission
  async function handleNameSubmit(data: { name: string }) {
    if (!pendingFilters) return;

    try {
      await createTopLevelView(data.name, pendingFilters);
      setShowNameDialog(false);
      setPendingFilters(null);
      nameForm.reset();
      notifySuccess({ message: "View created successfully!" });
    } catch (error) {
      notifyError({ error, message: "Failed to save view" });
    }
  }

  const handleViewGroupUpdate = async (
    viewGroup: TopLevelViewGroup,
    newName: string,
  ) => {
    await updateTopLevelViewName(viewGroup, newName);
    notifySuccess({ message: "View name updated successfully!" });
  };

  async function submit(data: FilterFormData) {
    onSubmit(convertFormFiltersToFilters(data.filters, categorizedFilters));
    onClose();
  }

  return (
    <ScrollAreaRoot>
      <ScrollAreaViewport className="max-h-[500px]">
        <Suspense fallback={null}>
          <TopLevelViewsSelector
            viewGroups={viewGroups}
            currentViewGroup={currentViewGroup}
            onSelect={handleViewGroupSelection}
            onUpdate={handleViewGroupUpdate}
            onDelete={deleteTopLevelView}
            isPendingSave={isPendingSave}
            isPendingDelete={isPendingDelete}
          />
        </Suspense>

        <Form
          {...form}
          className="flex max-w-[80vw] flex-col justify-between gap-2 overflow-y-scroll p-4"
          onSubmitValid={submit}
        >
          {fields.map((field, index) => (
            <CategorizedFilterFormField
              key={field.id}
              field={field}
              index={index}
              onRemove={remove}
              categorizedFilters={categorizedFilters}
            />
          ))}
          <div className="mt-1 flex items-center justify-between">
            <div className="flex gap-2">
              <div className="flex gap-2">
                <Button
                  icon={<PlusIcon className="fill-secondary-foreground" />}
                  intent="secondary"
                  type="button"
                  onClick={() =>
                    append({
                      category: "",
                      id: "",
                      operator: "",
                      value: "",
                      ors: [],
                    })
                  }
                >
                  Add filter
                </Button>
                <Button intent="primary" data-testid="filter-apply-button">
                  Apply
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  intent="text-link"
                  type="button"
                  onClick={() => {
                    onClear();
                    onClose();
                  }}
                >
                  Clear all
                </Button>
              </div>
            </div>

            <Button
              loading={isPendingSave}
              intent="secondary"
              type="button"
              onClick={onSave}
            >
              Save Changes to View
            </Button>
          </div>
        </Form>
      </ScrollAreaViewport>
      <ScrollBar orientation="vertical" />

      {/* Name dialog for new views */}
      <Dialog
        open={showNameDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowNameDialog(false);
            setPendingFilters(null);
            nameForm.reset();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Name your view</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Form {...nameForm} onSubmitValid={handleNameSubmit}>
              <div className="flex flex-col gap-2">
                <InputField error={nameForm.formState.errors.name?.message}>
                  <Input
                    {...nameForm.register("name", { required: true })}
                    autoFocus
                    placeholder="Enter view name"
                    disabled={isPendingSave}
                  />
                </InputField>
                <DialogFooter>
                  <Button
                    intent="primary"
                    type="submit"
                    loading={isPendingSave}
                  >
                    Save
                  </Button>
                  <Button
                    intent="secondary"
                    type="button"
                    onClick={() => setShowNameDialog(false)}
                  >
                    Cancel
                  </Button>
                </DialogFooter>
              </div>
            </Form>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </ScrollAreaRoot>
  );
}

interface CategorizedFilterFormFieldProps {
  field: FieldArrayWithId<FilterFormData>;
  index: number;
  onRemove: (index: number) => void;
  categorizedFilters: CategorizedAdditionalFilter[];
}

const CategorizedFilterFormField = ({
  field,
  index,
  onRemove,
  categorizedFilters,
}: CategorizedFilterFormFieldProps) => {
  const { register, setValue, watch, getValues } =
    useFormContext<FilterFormData>();

  const filters = getValues("filters");
  const currentFilter = filters[index];

  return (
    <div
      key={field.id}
      className="grid w-full grid-cols-1 items-start gap-2 md:grid-cols-4"
    >
      <div className="col-span-1 flex items-end gap-4">
        {index !== 0 && (
          <div className="col-span-1 mb-1.5 w-max rounded-md border border-stroke-line px-2 py-1 font-medium text-secondary-foreground text-xs">
            AND
          </div>
        )}
        <DropdownFormField
          {...register(`filters.${index}.category`, {
            onChange: (event) => {
              setValue(
                `filters.${index}.category`,
                event.target.value as Category,
              );
              setValue(`filters.${index}.id`, "");
              setValue(`filters.${index}.scope`, undefined);
              setValue(`filters.${index}.with`, undefined);
              setValue(`filters.${index}.isArray`, undefined);
              setValue(`filters.${index}.quantifier`, "any");
              setValue(`filters.${index}.operator`, "");
              setValue(`filters.${index}.value`, "");
              setValue(`filters.${index}.ors`, []);
            },
          })}
          label="Type"
          required
          options={[
            { label: "Endpoints", value: "endpoints" },
            { label: "Users", value: "users" },
            { label: "Inboxes", value: "inboxes" },
          ]}
          className="w-full"
          placeholder="Select a type"
          data-testid={`filter-type-${index}`}
        />
      </div>
      {currentFilter && (
        <FilterFormField
          field={currentFilter}
          index={index}
          scmCategory={getSCMCategory(currentFilter.category)}
          availableFilters={categorizedFilters.filter(
            (f) => f.category === watch(`filters.${index}.category`),
          )}
          disabled={!currentFilter.category}
          onRemove={onRemove}
          className="col-span-3 grid grid-cols-3"
        />
      )}
    </div>
  );
};

const getSCMCategory = (category: Category | ""): SCMCategory => {
  switch (category) {
    case "endpoints": {
      return SCMCategories.ENDPOINT;
    }
    case "inboxes": {
      return SCMCategories.INBOX;
    }
    case "users": {
      return SCMCategories.USER;
    }
    case "": {
      return SCMCategories.NONE;
    }
    default: {
      return assertNever(category);
    }
  }
};
